import { Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription, timeout } from 'rxjs';
import { CurrentSiteIdStore } from '../core/current-site-id.store';
import { EntityMutationBusService } from '../core/entity-mutations/entity-mutation-bus.service';
import { IdentityService } from '../core/identity/identity.service';
import { TargetSite } from '../core/target-site';
import { AgentContextService } from './agent-context.service';
import { AgentsChatService } from './agents-chat.service';
import {
  AgentChatMessageRequest,
  AgentChatSuggestionsRequest,
  ChatMention,
  ChatSelection,
  ChatStreamEvent,
  ConfirmationApproval,
  ConfirmationDecline,
  ContentBlock,
} from './agents-chat.model';
import {
  AgentMessage,
  PendingConfirmation,
  RenderedBlock,
  TemplateSelection,
  ToolCallStatus,
  TurnRating,
} from './agent-conversation.model';
import { mapToolResultToMutations } from './agent-tool-mutations';

// sessionStorage key for the cached conversation (tab-scoped; cleared on reset and sign-out).
const STORAGE_KEY = 'triggerbee-agent-conversation';

// Fail a turn whose stream goes silent this long so a stalled Gateway self-heals into the retry UX
// instead of spinning forever. Generous enough not to trip on a slow tool call or model latency.
const STREAM_INACTIVITY_TIMEOUT_MS = 90_000;

const TOOL_BUDGET_EXHAUSTED = 'tool_budget_exhausted';

interface PersistedConversation {
  email?: string;
  conversationId?: string;
  // The site the conversation was started on. The backend rebuilds memory filtered by site, so a
  // conversation can't cross sites; this lets a reload after a site switch discard the stale one.
  siteId?: number;
  messages: AgentMessage[];
  // Kept so Retry still works after a reload lands on an errored turn.
  lastRequest?: AgentChatMessageRequest;
}

// Holds the rendered conversation and drives a turn. Root singleton so it survives navigation while
// the global panel stays mounted.
@Injectable({ providedIn: 'root' })
export class AgentsConversationStore {
  private readonly contextService = inject(AgentContextService);
  private readonly chat = inject(AgentsChatService);
  private readonly identity = inject(IdentityService);
  private readonly router = inject(Router);
  private readonly mutationBus = inject(EntityMutationBusService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);

  private readonly messagesState = signal<AgentMessage[]>([]);
  private readonly streamingState = signal(false);
  // Follow-up chips for the latest completed turn; cleared when a new turn starts.
  private readonly suggestionsState = signal<string[]>([]);

  readonly messages: Signal<readonly AgentMessage[]> = this.messagesState.asReadonly();
  readonly streaming: Signal<boolean> = this.streamingState.asReadonly();
  readonly suggestions: Signal<readonly string[]> = this.suggestionsState.asReadonly();

  private conversationId: string | undefined;
  private subscription: Subscription | undefined;
  private turnSiteId: number | undefined;
  // Retained so an errored turn can be retried with the same inputs.
  private lastRequest: AgentChatMessageRequest | undefined;
  // The site the active conversation belongs to; a change means the user switched sites and the
  // conversation must start fresh (see startTurn).
  private conversationSiteId: number | undefined;
  // Text deltas are buffered and flushed once per animation frame, not per delta, so a long answer
  // doesn't trigger a change-detection + markdown re-parse on every token.
  private pendingText = new Map<number, string>();
  private rafHandle: number | undefined;

  constructor() {
    // Drop on sign-out so a later session on the same browser can't see the previous user's messages.
    this.identity.onSignout.pipe(takeUntilDestroyed()).subscribe(() => this.reset());

    // The backend rebuilds the model's memory from the saved conversationId, so restoring the
    // transcript keeps display and model aligned across a reload.
    this.restore();
  }

  send(message: string, mentions?: ChatMention[]): void {
    const text = message.trim();
    if (text.length === 0 || this.streamingState()) {
      return;
    }
    this.startTurn(text, undefined, undefined, undefined, mentions);
  }

  // Resume after a confirmation; the backend takes a non-LLM resume path off the approval.
  approve(confirmation: PendingConfirmation, label: string): void {
    if (this.streamingState()) {
      return;
    }
    // startTurn clears the pending confirmation card before opening the resume stream.
    this.startTurn(label, {
      toolCallId: confirmation.toolCallId,
      toolName: confirmation.toolName,
      arguments: confirmation.arguments,
    });
  }

  // Reject a gated action: resolve its pill to declined on the turn that raised it, then open a
  // turn that tells the backend it was declined (so later turns stay grounded) and streams the
  // model's acknowledgement. startTurn clears the confirmation card.
  deny(confirmation: PendingConfirmation, label: string): void {
    if (this.streamingState()) {
      return;
    }
    this.messagesState.update((list) =>
      list.map((message) =>
        message.confirmation?.toolCallId === confirmation.toolCallId
          ? { ...message, blocks: markToolDeclined(message.blocks ?? [], confirmation.toolCallId) }
          : message,
      ),
    );
    this.startTurn(label, undefined, undefined, {
      toolCallId: confirmation.toolCallId,
      toolName: confirmation.toolName,
      arguments: confirmation.arguments,
    });
  }

  // Model-driven turn carrying a structured selection so the backend creates from the exact id.
  pickTemplate(selection: TemplateSelection, label: string): void {
    if (this.streamingState()) {
      return;
    }
    this.startTurn(label, undefined, { kind: 'template', id: String(selection.templateId), label: selection.name });
  }

  private startTurn(
    text: string,
    approval?: ConfirmationApproval,
    selection?: ChatSelection,
    decline?: ConfirmationDecline,
    mentions?: ChatMention[],
  ): void {
    // A conversation is bound to the site it started on: the backend rebuilds its memory filtered by
    // site, so reusing it after a switch would silently drop context and split the record across
    // partitions. Start fresh instead.
    const site = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (
      this.conversationId !== undefined &&
      this.conversationSiteId !== undefined &&
      site !== this.conversationSiteId
    ) {
      this.reset();
    }
    this.conversationSiteId = site;
    // A fresh turn supersedes any still-pending confirmation, so drop its live Approve/Deny card.
    this.clearConfirmations();
    this.messagesState.update((list) => [
      ...list,
      { role: 'user', text, ...(mentions?.length ? { mentions } : {}) },
      { role: 'assistant', status: 'streaming', blocks: [], resumedApproval: approval !== undefined },
    ]);
    this.openStream({
      conversationId: this.conversationId ?? null,
      message: text,
      context: this.contextService.context(),
      approval: approval ?? null,
      selection: selection ?? null,
      decline: decline ?? null,
      mentions: mentions ?? null,
    });
  }

  // Reuse the exact last request and swap the errored turn for a fresh placeholder. Never retry an
  // approval resume: re-sending it re-runs the tool, which could double-execute if the stream dropped
  // after the tool had already run. The view hides Retry for those turns; this is the backstop.
  retry(): void {
    if (this.streamingState() || !this.lastRequest || this.lastRequest.approval) {
      return;
    }
    const site = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (this.conversationSiteId !== undefined && site !== this.conversationSiteId) {
      this.reset();
      return;
    }
    this.messagesState.update((list) => {
      const next = [...list];
      if (next[next.length - 1]?.role === 'assistant') {
        next[next.length - 1] = { role: 'assistant', status: 'streaming', blocks: [] };
      }
      return next;
    });
    // The backend files what a failed first turn ran under the id it assigned; without it the tools run again.
    this.openStream({ ...this.lastRequest, conversationId: this.conversationId ?? null });
  }

  private openStream(request: AgentChatMessageRequest): void {
    // Captured per turn so mutation events name the right site if the user switches mid-turn.
    this.turnSiteId = TargetSite.get() ?? this.currentSiteIdStore.get();
    this.lastRequest = request;
    this.suggestionsState.set([]);
    this.streamingState.set(true);
    this.subscription = this.chat
      .streamMessage(request)
      .pipe(timeout({ each: STREAM_INACTIVITY_TIMEOUT_MS }))
      .subscribe({
        next: (event) => this.apply(event),
        error: (error: unknown) => this.finishWithError(error),
        complete: () => this.finish(),
      });
  }

  cancel(): void {
    this.flushText();
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.streamingState.set(false);
    // 'stopped', not 'complete', so it doesn't read as finished; settle any still-spinning tool.
    this.updateAssistant((assistant) =>
      assistant.status === 'streaming'
        ? { ...assistant, status: 'stopped', blocks: settleRunningTools(assistant.blocks ?? []) }
        : assistant,
    );
    this.persist();
  }

  reset(): void {
    this.cancel();
    this.conversationId = undefined;
    this.conversationSiteId = undefined;
    this.messagesState.set([]);
    this.suggestionsState.set([]);
    this.clearStorage();
  }

  // Record thumbs up/down on a settled assistant turn. Optimistic: reflect the choice immediately
  // and revert if the backend rejects it (e.g. the turn aged out). A no-op when the rating is
  // unchanged or the turn has no id / no conversation yet.
  rate(messageId: string, rating: TurnRating): void {
    const conversationId = this.conversationId;
    const target = this.messagesState().find((message) => message.messageId === messageId);
    if (!conversationId || !target || target.rating === rating) {
      return;
    }
    const previous = target.rating;
    this.setRating(messageId, rating);
    this.persist();
    void this.chat
      .rateTurn(conversationId, messageId, rating)
      .then((ok) => {
        if (!ok) {
          this.setRating(messageId, previous);
          this.persist();
        }
      })
      .catch(() => {
        this.setRating(messageId, previous);
        this.persist();
      });
  }

  private setRating(messageId: string, rating: TurnRating | undefined): void {
    this.messagesState.update((list) =>
      list.map((message) => (message.messageId === messageId ? { ...message, rating } : message)),
    );
  }

  private apply(event: ChatStreamEvent): void {
    if (event.type === 'ping') {
      return;
    }
    // Commit any frame-buffered text before a structural event so ordering stays exact; only
    // consecutive text deltas are coalesced.
    if (event.type !== 'content_block_delta' || event.delta.type !== 'text_delta') {
      this.flushText();
    }
    switch (event.type) {
      case 'message_start': {
        this.conversationId = event.conversationId;
        // Stamp the turn id onto the streaming assistant message so it can be rated once settled.
        const messageId = event.messageId;
        this.updateAssistant((assistant) => ({ ...assistant, messageId }));
        break;
      }
      case 'content_block_start': {
        const rendered = toRenderedBlock(event.index, event.block);
        this.updateAssistant((assistant) => ({
          ...assistant,
          blocks:
            rendered.kind === 'tool_result'
              ? mergeToolResult(assistant.blocks ?? [], rendered)
              : upsertBlock(assistant.blocks ?? [], rendered),
        }));
        if (rendered.kind === 'tool_result') {
          this.resolveApprovedCall(rendered);
          this.applyToolResultEffects(rendered);
        }
        break;
      }
      case 'content_block_delta': {
        if (event.delta.type === 'text_delta') {
          this.bufferText(event.index, event.delta.text);
        }
        break;
      }
      case 'confirmation_required': {
        this.updateAssistant((assistant) => ({
          ...assistant,
          status: 'complete',
          // Terminal turn, no tool_result coming: mark the gated pill 'awaiting'.
          blocks: markAwaitingConfirmation(assistant.blocks ?? [], event.toolCallId),
          confirmation: {
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            summary: event.summary,
            arguments: event.arguments,
            changes: event.changes,
          },
        }));
        break;
      }
      case 'error': {
        this.updateAssistant((assistant) => ({
          ...assistant,
          status: 'error',
          error: event.message,
          errorCode: event.code,
        }));
        break;
      }
      case 'content_block_stop':
      case 'message_delta':
      case 'message_stop': {
        break;
      }
    }
  }

  private finish(): void {
    this.flushText();
    this.subscription = undefined;
    this.streamingState.set(false);
    this.updateAssistant((assistant) => ({
      ...assistant,
      status: assistant.status === 'streaming' ? 'complete' : assistant.status,
      blocks: settleRunningTools(assistant.blocks ?? []),
    }));
    this.persist();
    this.fetchSuggestions();
  }

  // Chips for the just-finished turn; only on a clean completion, dropped if a new turn started.
  private fetchSuggestions(): void {
    const list = this.messagesState();
    const assistant = list[list.length - 1];
    const user = list[list.length - 2];
    if (!assistant || assistant.role !== 'assistant' || assistant.status !== 'complete' || assistant.confirmation) {
      return;
    }
    const assistantMessage = (assistant.blocks ?? [])
      .filter((block) => block.kind === 'text' && !!block.text)
      .map((block) => block.text)
      .join('\n\n')
      .trim();
    const toolsUsed = (assistant.blocks ?? [])
      .filter((block) => block.kind === 'tool_use' && !!block.toolName)
      .map((block) => block.toolName as string);
    if (assistantMessage.length === 0 && toolsUsed.length === 0) {
      return;
    }

    const request: AgentChatSuggestionsRequest = {
      conversationId: this.conversationId ?? null,
      context: this.contextService.context(),
      userMessage: user?.role === 'user' ? (user.text ?? '') : '',
      assistantMessage,
      toolsUsed,
    };
    const turnCount = list.length;
    void this.chat.getSuggestions(request).then((suggestions) => {
      if (this.messagesState().length === turnCount && !this.streamingState()) {
        this.suggestionsState.set(suggestions);
      }
    });
  }

  private finishWithError(error: unknown): void {
    this.flushText();
    this.subscription = undefined;
    this.streamingState.set(false);
    const messageText = error instanceof Error ? error.message : 'The assistant could not be reached.';
    this.updateAssistant((assistant) => {
      const settled = { ...assistant, blocks: settleRunningTools(assistant.blocks ?? []) };
      return assistant.status === 'streaming'
        ? { ...settled, status: 'error', error: messageText, errorCode: 'transport' }
        : settled;
    });
    this.persist();
  }

  // The approved run reuses the gated call's id, so its outcome resolves the paused pill on the asking turn.
  private resolveApprovedCall(result: RenderedBlock): void {
    this.messagesState.update((list) => {
      if (!list[list.length - 1]?.resumedApproval) {
        return list;
      }
      let gated = -1;
      for (let index = list.length - 2; index >= 0 && gated < 0; index--) {
        if (
          (list[index].blocks ?? []).some(
            (block) =>
              block.kind === 'tool_use' && block.toolId === result.toolUseId && block.toolStatus === 'awaiting',
          )
        ) {
          gated = index;
        }
      }
      if (gated < 0) {
        return list;
      }
      const status = toolStatusFor(result);
      return list.map((message, index) =>
        index === gated
          ? {
              ...message,
              blocks: (message.blocks ?? []).map((block) =>
                block.kind === 'tool_use' && block.toolId === result.toolUseId
                  ? { ...block, toolStatus: status }
                  : block,
              ),
            }
          : message,
      );
    });
  }

  private clearConfirmations(): void {
    this.messagesState.update((list) =>
      list.map((message) => (message.confirmation ? { ...message, confirmation: undefined } : message)),
    );
  }

  private updateAssistant(project: (assistant: AgentMessage) => AgentMessage): void {
    this.messagesState.update((list) => {
      const next = [...list];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = project(last);
      }
      return next;
    });
  }

  // Accumulate a text delta and schedule a single flush on the next frame.
  private bufferText(index: number, text: string): void {
    this.pendingText.set(index, (this.pendingText.get(index) ?? '') + text);
    if (this.rafHandle === undefined) {
      this.rafHandle = requestAnimationFrame(() => {
        this.rafHandle = undefined;
        this.flushText();
      });
    }
  }

  // Apply all buffered text deltas in one update, then clear the buffer. Safe to call eagerly: a
  // no-op when nothing is buffered.
  private flushText(): void {
    if (this.rafHandle !== undefined) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = undefined;
    }
    if (this.pendingText.size === 0) {
      return;
    }
    const pending = this.pendingText;
    this.pendingText = new Map();
    this.updateAssistant((assistant) => {
      let blocks = assistant.blocks ?? [];
      for (const [index, text] of pending) {
        blocks = appendText(blocks, index, text);
      }
      return { ...assistant, blocks };
    });
  }

  // A successful tool result may have mutated data: publish mutation events so open pages refetch,
  // and run any follow-up navigation.
  private applyToolResultEffects(result: RenderedBlock): void {
    if (result.isError) {
      return;
    }
    const toolUse = this.findToolUse(result.toolUseId);
    if (toolUse?.toolName === undefined) {
      return;
    }
    const outcome = mapToolResultToMutations(toolUse.toolName, result.content, this.turnSiteId, toolUse.input);
    if (outcome === undefined) {
      return;
    }
    for (const event of outcome.events) {
      this.mutationBus.publish(event);
    }
    const site = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (outcome.navigateTo && site === this.turnSiteId) {
      void this.router.navigate(outcome.navigateTo);
    }
  }

  private findToolUse(toolUseId: string | undefined): RenderedBlock | undefined {
    if (!toolUseId) {
      return undefined;
    }
    const list = this.messagesState();
    const assistant = list[list.length - 1];
    return assistant?.blocks?.find((item) => item.kind === 'tool_use' && item.toolId === toolUseId);
  }

  // Persist at turn boundaries (not per delta). Best-effort: sessionStorage may be unavailable/full.
  private persist(): void {
    const messages = this.messagesState();
    if (messages.length === 0) {
      return;
    }
    const snapshot: PersistedConversation = {
      email: this.identity.currentIdentity?.email,
      conversationId: this.conversationId,
      siteId: this.conversationSiteId,
      messages: [...messages],
      lastRequest: this.lastRequest,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // best-effort
    }
  }

  private restore(): void {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) {
      return;
    }
    try {
      const snapshot = JSON.parse(raw) as PersistedConversation;
      // Different account (shouldn't happen in one tab session); discard.
      const currentEmail = this.identity.currentIdentity?.email;
      if (currentEmail && snapshot.email && snapshot.email !== currentEmail) {
        this.clearStorage();
        return;
      }
      // Switched sites since the tab was last open: the conversation belongs to the old site, so
      // don't resurrect it against the new one.
      const currentSite = TargetSite.get() ?? this.currentSiteIdStore.get();
      if (currentSite !== undefined && snapshot.siteId !== undefined && snapshot.siteId !== currentSite) {
        this.clearStorage();
        return;
      }
      if (Array.isArray(snapshot.messages) && snapshot.messages.length > 0) {
        this.conversationId = snapshot.conversationId ?? undefined;
        this.conversationSiteId = snapshot.siteId;
        this.lastRequest = snapshot.lastRequest;
        this.messagesState.set(
          snapshot.messages.map((message): AgentMessage =>
            message.status === 'streaming'
              ? { ...message, status: 'stopped', blocks: settleRunningTools(message.blocks ?? []) }
              : message,
          ),
        );
      }
    } catch {
      this.clearStorage();
    }
  }

  private clearStorage(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Best-effort.
    }
  }
}

function toRenderedBlock(index: number, block: ContentBlock): RenderedBlock {
  switch (block.type) {
    case 'text': {
      return { kind: 'text', index, text: '' };
    }
    case 'tool_use': {
      return {
        kind: 'tool_use',
        index,
        toolId: block.id,
        toolName: block.name,
        input: block.input,
        toolStatus: 'running',
      };
    }
    case 'tool_result': {
      return { kind: 'tool_result', index, toolUseId: block.toolUseId, isError: block.isError, content: block.content };
    }
  }
}

// Fold a tool_result into its tool_use block so they render as one item; keep it standalone if no match.
function mergeToolResult(blocks: readonly RenderedBlock[], result: RenderedBlock): RenderedBlock[] {
  const target = blocks.find((block) => block.kind === 'tool_use' && block.toolId === result.toolUseId);
  if (!target) {
    return upsertBlock(blocks, result);
  }
  return blocks.map((block) =>
    block === target
      ? { ...block, toolStatus: toolStatusFor(result), isError: result.isError, content: result.content }
      : block,
  );
}

function toolStatusFor(result: RenderedBlock): ToolCallStatus {
  if (!result.isError) {
    return 'done';
  }
  const content = result.content;
  if (typeof content !== 'object' || content === null) {
    return 'error';
  }
  if ('code' in content && content.code === TOOL_BUDGET_EXHAUSTED) {
    return 'skipped';
  }
  // A validation error is the change refused as asked, which the reply explains; nothing failed.
  return 'category' in content && content.category === 'validation' ? 'refused' : 'error';
}

function upsertBlock(blocks: readonly RenderedBlock[], block: RenderedBlock): RenderedBlock[] {
  const next = blocks.filter((existing) => existing.index !== block.index);
  next.push(block);
  next.sort((a, b) => a.index - b.index);
  return next;
}

function appendText(blocks: readonly RenderedBlock[], index: number, text: string): RenderedBlock[] {
  return blocks.map((block) =>
    block.index === index && block.kind === 'text' ? { ...block, text: (block.text ?? '') + text } : block,
  );
}

// Flip the gated tool pill 'running' -> 'awaiting'; no tool_result comes this turn.
function markAwaitingConfirmation(blocks: readonly RenderedBlock[], toolCallId: string): RenderedBlock[] {
  return blocks.map((block) =>
    block.kind === 'tool_use' && block.toolId === toolCallId && block.toolStatus === 'running'
      ? { ...block, toolStatus: 'awaiting' }
      : block,
  );
}

// Settle still-spinning pills to a neutral 'awaiting' (aborted stream, result never arrives).
function settleRunningTools(blocks: readonly RenderedBlock[]): RenderedBlock[] {
  return blocks.map((block) =>
    block.kind === 'tool_use' && block.toolStatus === 'running' ? { ...block, toolStatus: 'awaiting' } : block,
  );
}

// Resolve the gated pill to 'declined' so it's not a stuck pause.
function markToolDeclined(blocks: readonly RenderedBlock[], toolCallId: string): RenderedBlock[] {
  return blocks.map((block) =>
    block.kind === 'tool_use' && block.toolId === toolCallId ? { ...block, toolStatus: 'declined' } : block,
  );
}
